import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { format } from "date-fns";
import { useTheme, useMediaQuery, Box, Typography, 
    FormControl, MenuItem, InputLabel, Select,       
} from '@mui/material';
import useAuthCheck from 'hooks/useAuthCheck';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { LAYOUT_HEIGHT, DIVERT_RETURN_STATUS } from 'state/constants';
import { filterFlights } from 'state/helpers';
import { countFlightsByField, countStackedFlightsBy2Fields, countBlockHoursByTableau } from './utils';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FilterButton from 'components/FilterButton';
import ScrollToTop from 'components/ScrollToTop';
import ChartDialog from 'components/chartNivo/ChartDialog';
import FlightFilterDialog from 'components/FlightFilterDialog';
import PieChart from 'components/chartNivo/PieChart';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';
import SortStackedBarChart from 'components/chartNivo/SortStackedBarChart';
import CommonTableau from './CommonTableau';
import { boxStyles as styles } from 'components/CustomStyles';



const typeOptions = [
  { value: "actual", label: "Actual" },
  { value: "schedule", label: "Schedule" },
  { value: "estimated", label: "Estimated" },
];

const blockHoursMap = {
    actual: "actualBlockHours",
    schedule: "scheduleBlockHours",
    estimated: "estimatedBlockHours"
};

const displayOptions = [
    { value: "chart", label: "Chart" },
    { value: "tableau", label: "Tableau" },
];

const viewOptions = {
    chart: [
        { value: "blockHoursByFlightDate", label: "by Date" },
        { value: "blockHoursByDep", label: "by Port" },
        { value: "blockHoursBySector", label: "by Sector" },
        { value: "blockHoursBySectorType", label: "by Sector Type" },
        { value: "blockHoursByFlightType", label: "by Flight Type" },
        { value: "blockHoursByFlightReg", label: "by Flight Reg" },
        { value: "blockHoursByACType", label: "by AC Type" },
        { value: "blockHoursByCountry", label: "by Country" },
        { value: "blockHoursByFlightNo", label: "by Flight No" },
    ],
    tableau: [
        { value: "byDate", label: "by Date" },
        { value: "byDateDep", label: "by Date & Port" },
        { value: "byDateSector", label: "by Date & Sector" },
        { value: "byDateSectorType", label: "by Date & Sector Type" },
        { value: "byDateFlightType", label: "by Date & Flight Type" },
        { value: "byDateFlightReg", label: "by Date & Flight Reg" },
        { value: "byDateACType", label: "by Date & AC Type" },
        { value: "byDateCountry", label: "by Date & Country" },
        { value: "byDateFlightNo", label: "by Date & Flight No" },

        { value: "byDep", label: "by Port" },
        { value: "bySector", label: "by Sector" },
        { value: "bySectorType", label: "by Sector Type" },
        { value: "byFlightType", label: "by Flight Type" },

        { value: "byFlightReg", label: "by Flight Reg" },
        { value: "byFlightRegDep", label: "by Reg & Port" },
        { value: "byFlightRegSector", label: "by Reg & Sector" },
        { value: "byFlightRegSectorType", label: "by Reg & Sector Type" },
        { value: "byFlightRegFlightType", label: "by Reg & Flight Type" },
        { value: "byFlightRegCountry", label: "by Reg & Country" },

        { value: "byACType", label: "by AC Type" },
        { value: "byACTypeDep", label: "by AC & Port" },
        { value: "byACTypeSector", label: "by AC & Sector" },
        { value: "byACTypeSectorType", label: "by AC & Sector Type" },
        { value: "byACTypeFlightType", label: "by AC & Flight Type" },
        { value: "byACTypeCountry", label: "by AC & Country" },

        { value: "byCountry", label: "by Country" },
        { value: "byFlightNo", label: "by Flight No" },
    ],
};


const BlockHoursChart = () => {    
    useAuthCheck();
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
        
    const { startDate, endDate } = useDateRangeContext();

    const scrollRef = useRef(null);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState(null);
    const [chartOpen, setChartOpen] = useState(false);  
    const [selectedChartIndex, setSelectedChartIndex] = useState(0);
    
    const [type, setType] = useState('actual');
    const [displayType, setDisplayType] = useState('chart');
    const [view, setView] = useState('blockHoursByFlightDate');

    useEffect(() => {
        const options = viewOptions[displayType];
        if (!options?.length) return;
        const isValid = options.some(opt => opt.value === view);
        if (!isValid) setView(options[0].value);
    }, [displayType, view]);

    const safeView = useMemo(() => {
        const options = viewOptions[displayType] || [];
        return options.some(o => o.value === view)
            ? view
            : options[0]?.value || '';
    }, [view, displayType]);

    const flightsData = useSelector((state) => state.flights); 

    const cacheRef = useRef({});    
    const flights = useMemo(() => {
        return flightsData.filter(f => {
            const status = f.flightStatus?.toLowerCase() || '';

            return (
                !status.includes('cancelled') &&
                !status.includes('deleted') &&
                !DIVERT_RETURN_STATUS.includes(f.flightStatus)
            );
        });
    }, [flightsData]);

    const filteredFlights = useMemo(() => {
        return filterFlights({ flights, filters, timeMode });
    }, [flights, filters, timeMode]);
          
    const blockHours = blockHoursMap[type] ?? "blockHours";
    
    const computedData = useMemo(() => {
        const cacheKey = `${view}-${timeMode}-${filteredFlights.length}`;

        if (cacheRef.current[cacheKey]) {
            return cacheRef.current[cacheKey];
        }

        const flightDate = timeMode === "UTC" ? "flightDate" : "flightDateLocalBase";
        const f = filteredFlights || [];
        let result = {};

        switch (view) {
            case "blockHoursByFlightDate":
                result = {
                    blockHoursByFlightDate: countFlightsByField(f, flightDate, "barline", "BH", "BHAvg", blockHours),
                    blockHoursByFlightDateSectorType: countStackedFlightsBy2Fields(f, flightDate, "sectorType", blockHours),
                    blockHoursByFlightDateFlightType: countStackedFlightsBy2Fields(f, flightDate, "flightType", blockHours),
                    // blockHoursByFlightDateFlightReg: countStackedFlightsBy2Fields(f, flightDate, "flightReg", blockHours),
                    blockHoursByFlightDateACType: countStackedFlightsBy2Fields(f, flightDate, "acType", blockHours),
                    // blockHoursByFlightDateCountry: countStackedFlightsBy2Fields(f, flightDate, "depCountryName", blockHours),
                    // blockHoursByFlightDateFlightNo: countStackedFlightsBy2Fields(f, flightDate, "flightNo", blockHours),
                };
                break;
            case "blockHoursByDep":
                result = {
                    blockHoursByDep: countFlightsByField(f, "dep", "barline", "BH", "BHAvg", blockHours),
                    blockHoursByDepSectorType: countStackedFlightsBy2Fields(f, "dep", "sectorType", blockHours),
                    blockHoursByDepFlightType: countStackedFlightsBy2Fields(f, "dep", "flightType", blockHours),
                };
                break;
            case "blockHoursBySector":
                result = {
                    blockHoursBySector: countFlightsByField(f, "sector", "barline", "BH", "BHAvg", blockHours),
                    blockHoursBySectorFlightType: countStackedFlightsBy2Fields(f, "sector", "flightType", blockHours),
                };
                break;
            case "blockHoursBySectorType":
                result = {
                    blockHoursBySector_Type: countFlightsByField(f, "sectorType", "pie", "BH", "BHAvg", blockHours),
                    blockHoursBySectorType: countFlightsByField(f, "sectorType", "barline", "BH", "BHAvg", blockHours),
                    blockHoursBySectorTypeFlightType: countStackedFlightsBy2Fields(f, "sectorType", "flightType", blockHours),
                    blockHoursBySectorTypeACType: countStackedFlightsBy2Fields(f, "sectorType", "acType", blockHours),
                    blockHoursBySectorTypeCountry: countStackedFlightsBy2Fields(f, "sectorType", "depCountryName", blockHours),
                };
                break;
            case "blockHoursByFlightType":
                result = {
                    blockHoursByFlight_Type: countFlightsByField(f, "flightType", "pie", "BH", "BHAvg", blockHours),
                    blockHoursByFlightType: countFlightsByField(f, "flightType", "barline", "BH", "BHAvg", blockHours),
                    blockHoursByFlightTypeSectorType: countStackedFlightsBy2Fields(f, "flightType", "sectorType", blockHours),
                };
                break;
            case "blockHoursByFlightReg":
                result = {
                    blockHoursByFlight_Reg: countFlightsByField(f, "flightReg", "pie", "BH", "BHAvg", blockHours),
                    blockHoursByFlightReg: countFlightsByField(f, "flightReg", "barline", "BH", "BHAvg", blockHours),
                    blockHoursByFlightRegSectorType: countStackedFlightsBy2Fields(f, "flightReg", "sectorType", blockHours),
                    blockHoursByFlightRegFlightType: countStackedFlightsBy2Fields(f, "flightReg", "flightType", blockHours),
                    blockHoursByFlightRegCountry: countStackedFlightsBy2Fields(f, "flightReg", "depCountryName", blockHours),
                };
                break;
            case "blockHoursByACType":
                result = {
                    blockHoursByAC_Type: countFlightsByField(f, "acType", "pie", "BH", "BHAvg", blockHours),
                    blockHoursByACType: countFlightsByField(f, "acType", "barline", "BH", "BHAvg", blockHours),
                    blockHoursByACTypeSectorType: countStackedFlightsBy2Fields(f, "acType", "sectorType", blockHours),
                    blockHoursByACTypeFlightType: countStackedFlightsBy2Fields(f, "acType", "flightType", blockHours),
                    blockHoursByACTypeCountry: countStackedFlightsBy2Fields(f, "acType", "depCountryName", blockHours),
                };
                break;
            case "blockHoursByCountry":
                result = {
                    blockHoursByCountry_Name: countFlightsByField(f, "depCountryName", "pie", "BH", "BHAvg", blockHours),
                    blockHoursByCountryName: countFlightsByField(f, "depCountryName", "barline", "BH", "BHAvg", blockHours),
                    blockHoursByCountrySectorType: countStackedFlightsBy2Fields(f, "depCountryName", "sectorType", blockHours),
                    blockHoursByCountryFlightType: countStackedFlightsBy2Fields(f, "depCountryName", "flightType", blockHours),
                    blockHoursByCountryACType: countStackedFlightsBy2Fields(f, "depCountryName", "acType", blockHours),
                };
                break;
            case "blockHoursByFlightNo":
                result = {
                    blockHoursByFlightNo: countFlightsByField(f, "flightNo", "barline", "BH", "BHAvg", blockHours),
                    blockHoursByFlightNoSectorType: countStackedFlightsBy2Fields(f, "flightNo", "sectorType", blockHours),
                    blockHoursByFlightNoFlightType: countStackedFlightsBy2Fields(f, "flightNo", "flightType", blockHours),
                    blockHoursByFlightNoCountry: countStackedFlightsBy2Fields(f, "flightNo", "depCountryName", blockHours),
                };
                break;
            default:
                break;
        }

        cacheRef.current[cacheKey] = result;
        return result;
    }, [timeMode, view, filteredFlights, blockHours]);
    
    const tableauData = useMemo(() => {
        if (displayType !== 'tableau') return [];
        const flightDate = timeMode === "UTC" ? "flightDate" : "flightDateLocalBase";
        const f = filteredFlights || [];

        const fieldMap = {
            byDate:                [flightDate, null],
            byDateDep:             [flightDate, 'dep'],
            byDateSector:          [flightDate, 'sector'],
            byDateSectorType:      [flightDate, 'sectorType'],
            byDateFlightType:      [flightDate, 'flightType'],
            byDateFlightReg:       [flightDate, 'flightReg'],
            byDateACType:          [flightDate, 'acType'],
            byDateCountry:         [flightDate, 'depCountryName'],
            byDateFlightNo:        [flightDate, 'flightNo'],
            byDep:                 ['dep', null],
            bySector:              ['sector', null],
            bySectorType:          ['sectorType', null],
            byFlightType:          ['flightType', null],
            byFlightReg:           ['flightReg', null],
            byFlightRegDep:        ['flightReg', 'dep'],
            byFlightRegSector:     ['flightReg', 'sector'],
            byFlightRegSectorType: ['flightReg', 'sectorType'],
            byFlightRegFlightType: ['flightReg', 'flightType'],
            byFlightRegCountry:    ['flightReg', 'depCountryName'],
            byACType:              ['acType', null],
            byACTypeDep:           ['acType', 'dep'],
            byACTypeSector:        ['acType', 'sector'],
            byACTypeSectorType:    ['acType', 'sectorType'],
            byACTypeFlightType:    ['acType', 'flightType'],
            byACTypeCountry:       ['acType', 'depCountryName'],
            byCountry:             ['depCountryName', null],
            byFlightNo:            ['flightNo', null],
        };

        const fields = fieldMap[safeView];
        if (!fields) return [];
        return countBlockHoursByTableau(f, fields[0], fields[1], blockHours);
    }, [displayType, safeView, timeMode, filteredFlights, blockHours]);

    const handleFilterOpen = useCallback(() => setIsFilterDialogOpen(true), []);
    const handleFilterClose = useCallback(() => setIsFilterDialogOpen(false), []);
    const handleOpen = useCallback(() => setChartOpen(true), []);
    const handleClose = useCallback(() => setChartOpen(false), []);

    const handleFilterApply = useCallback((filterValues) => {
        setFilters(filterValues); 
    }, []);

    const handleTypeChange = useCallback((e) => {
        setType(e.target.value);
    }, []);

    const {
        acTypeOptions,
        flightRegOptions,
        sectorTypeOptions,
        departureTypeOptions,
        arrivalTypeOptions,
    } = useMemo(() => {
        if (!Array.isArray(flights)) {
            return {
                acTypeOptions: [],
                flightRegOptions: [],
                sectorTypeOptions: [],
                departureTypeOptions: [],
                arrivalTypeOptions: [],
            };
        }

        const sorted = (key) =>
            [...new Set(flights.map((f) => f[key]).filter(Boolean))].sort();

        return {
            acTypeOptions: sorted("acType"),
            flightRegOptions: sorted("flightReg"),
            sectorTypeOptions: sorted("sectorType"),
            departureTypeOptions: sorted("departureType"),
            arrivalTypeOptions: sorted("arrivalType"),
        };
    }, [flights]);

    const period = useMemo(() =>
        `${startDate ? format(startDate, 'ddMMMyy') : ''} - ${endDate ? format(endDate, 'ddMMMyy') : ''}`,
    [startDate, endDate]);
    const defaultTitle = useMemo(() => `Block Hours ${period}`, [period]);

    const chartMap = useMemo(() => {
        switch (view) {
            case "blockHoursByFlightDate":
                return [
                    {
                        component: SortBarMultiLinesChart,
                        props: {
                            data: computedData.blockHoursByFlightDate,
                            title: defaultTitle,
                            bottomLegend: "Date",
                            leftLegend: "Hours",
                            digit: 0,
                            barTranslateXOffSet: 120,
                            lineTranslateXOffSet: -5,
                        },
                    },
                    { 
                        component: SortStackedBarChart, 
                        props: {
                            data: computedData.blockHoursByFlightDateSectorType,
                            title: defaultTitle,
                            bottomLegend: "Date",
                            leftLegend: "Hours",
                            digit: 0,
                            itemWidthOffSet: 10,
                        },
                    },
                    { 
                        component: SortStackedBarChart, 
                        props: {
                            data: computedData.blockHoursByFlightDateFlightType,
                            title: defaultTitle,
                            bottomLegend: "Date",
                            leftLegend: "Hours",
                            digit: 0,
                            translateXOffSet: -15, 
                            itemWidthOffSet: 25,
                        },
                    },
                    // { 
                    //     component: SortStackedBarChart, 
                    //     props: {
                    //         data: computedData.blockHoursByFlightDateFlightReg,
                    //         title: defaultTitle,
                    //         bottomLegend: "Date",
                    //         leftLegend: "Hours",
                    //         digit: 0,
                    //     },
                    // },
                    { 
                        component: SortStackedBarChart, 
                        props: {
                            data: computedData.blockHoursByFlightDateACType,
                            title: defaultTitle,
                            bottomLegend: "Date",
                            leftLegend: "Hours",
                            digit: 0,
                        },
                    },
                    // { 
                    //     component: SortStackedBarChart, 
                    //     props: {
                    //         data: computedData.blockHoursByFlightDateCountry,
                    //         title: defaultTitle,
                    //         bottomLegend: "Date",
                    //         leftLegend: "Hours",
                    //         digit: 0,
                    //     },
                    // },
                    // { 
                    //     component: SortStackedBarChart, 
                    //     props: {
                    //         data: computedData.blockHoursByFlightDateFlightNo,
                    //         title: defaultTitle,
                    //         bottomLegend: "Date",
                    //         leftLegend: "Hours",
                    //         digit: 0,
                    //         itemWidthOffSet: -35 
                    //     },
                    // },
                ];

            case "blockHoursByDep":
                return [
                    {
                        component: SortBarMultiLinesChart,
                        props: {
                            data: computedData.blockHoursByDep,
                            title: defaultTitle,
                            bottomLegend: "Port",
                            leftLegend: "Hours",
                            digit: 0,
                            barTranslateXOffSet: 120,
                            lineTranslateXOffSet: -5,
                        },
                    },
                    { 
                        component: SortStackedBarChart, 
                        props: {
                            data: computedData.blockHoursByDepSectorType,
                            title: defaultTitle,
                            bottomLegend: "Port",
                            leftLegend: "Hours",
                            digit: 0,
                            itemWidthOffSet: 10,
                        },
                    },
                    { 
                        component: SortStackedBarChart, 
                        props: {
                            data: computedData.blockHoursByDepFlightType,
                            title: defaultTitle,
                            bottomLegend: "Port",
                            leftLegend: "Hours",
                            digit: 0,
                            translateXOffSet: -15, 
                            itemWidthOffSet: 25,
                        },
                    },
                ];

            case "blockHoursBySector":
                return [
                    {
                        component: SortBarMultiLinesChart,
                        props: {
                            data: computedData.blockHoursBySector,
                            title: defaultTitle,
                            bottomLegend: "Sector",
                            leftLegend: "Hours",
                            digit: 0,
                            bottomOffSet: 10,
                            bottomLegendOffSet: 10,
                            barTranslateXOffSet: 120,
                            lineTranslateXOffSet: -5,
                        },
                    },            
                    { 
                        component: SortStackedBarChart, 
                        props: {
                            data: computedData.blockHoursBySectorFlightType,
                            title: defaultTitle,
                            bottomLegend: "Sector",
                            leftLegend: "Hours",
                            digit: 0,
                            bottomOffSet: 10,
                            bottomLegendOffSet: 10,
                            translateXOffSet: -15, 
                            itemWidthOffSet: 25,
                        },
                    },            
                ];

            case "blockHoursBySectorType":
                return [
                    {
                        component: PieChart,
                        props: {
                            data: computedData.blockHoursBySector_Type,
                            title: defaultTitle,
                            bottomLegend: "Sector Type",
                            leftLegend: "Block Hours",
                            digit: 0,
                        },
                        style: {},
                    },
                    {
                        component: SortBarMultiLinesChart,
                        props: {
                            data: computedData.blockHoursBySectorType,
                            title: defaultTitle,
                            bottomLegend: "Sector Type",
                            leftLegend: "Hours",
                            isHorizontalBottomLegend: true,
                            digit: 0,
                            bottomOffSet: -30,
                            bottomLegendOffSet: -30,
                            barTranslateXOffSet: 120,
                            lineTranslateXOffSet: -5,
                        },
                    },
                    { 
                        component: SortStackedBarChart, 
                        props: {
                            data: computedData.blockHoursBySectorTypeFlightType,
                            title: defaultTitle,
                            bottomLegend: "Sector Type",
                            leftLegend: "Hours",
                            isHorizontalBottomLegend: true,
                            digit: 0,
                            bottomOffSet: -30,
                            bottomLegendOffSet: -30,
                            translateXOffSet: -15, 
                            itemWidthOffSet: 25,
                        },
                    },
                    { 
                        component: SortStackedBarChart, 
                        props: {
                            data: computedData.blockHoursBySectorTypeACType,
                            title: defaultTitle,
                            bottomLegend: "Sector Type",
                            leftLegend: "Hours",
                            isHorizontalBottomLegend: true,
                            digit: 0,
                            bottomOffSet: -30,
                            bottomLegendOffSet: -30,
                            translateXOffSet: -15,
                            itemWidthOffSet: -35,
                        },
                    },
                    { 
                        component: SortStackedBarChart, 
                        props: {
                            data: computedData.blockHoursBySectorTypeCountry,
                            title: defaultTitle,
                            bottomLegend: "Sector Type",
                            leftLegend: "Hours",
                            isHorizontalBottomLegend: true,
                            digit: 0,
                            bottomOffSet: -30,
                            bottomLegendOffSet: -30,
                        },
                    },       
                ];

            case "blockHoursByFlightType":
                return [
                    {
                        component: PieChart,
                        props: {
                            data: computedData.blockHoursByFlight_Type,
                            title: defaultTitle,
                            bottomLegend: "Flight Type",
                            leftLegend: "Block Hours",
                            isHorizontalBottomLegend: true,
                            digit: 0,
                            itemWidthOffSet: 20,
                        },
                        style: {},
                    },
                    {
                        component: SortBarMultiLinesChart,
                        props: {
                            data: computedData.blockHoursByFlightType,
                            title: defaultTitle,
                            bottomLegend: "Flight Type",
                            leftLegend: "Hours",
                            isHorizontalBottomLegend: !isMobile,
                            digit: 0,
                            bottomOffSet: !isMobile ? -30 : 50,
                            bottomLegendOffSet: !isMobile ? -30 : 50,
                            barTranslateXOffSet: 120,
                            lineTranslateXOffSet: -5,
                        },
                    },    
                    { 
                        component: SortStackedBarChart, 
                        props: {
                            data: computedData.blockHoursByFlightTypeSectorType,
                            title: defaultTitle,
                            bottomLegend: "Flight Type",
                            leftLegend: "Hours",
                            isHorizontalBottomLegend: !isMobile,
                            digit: 0,
                            bottomOffSet: !isMobile ? -30 : 50,
                            bottomLegendOffSet: !isMobile ? -30 : 50,
                            itemWidthOffSet: 10,
                        },
                    },                  
                ];

            case "blockHoursByFlightReg":
                return [
                    ...(computedData.blockHoursByFlight_Reg?.length < 10 ? [{
                        component: PieChart,
                        props: {
                            data: computedData.blockHoursByFlight_Reg,
                            title: defaultTitle,
                            bottomLegend: "Flight Reg",
                            leftLegend: "Block Hours",
                            digit: 0,
                            itemWidthOffSet: isMobile ? -35 : -25,
                            translateXOffSet: isMobile ? -15 : 0,
                        },
                        style: {},
                    }] : []),
                    {
                        component: SortBarMultiLinesChart,
                        props: {
                            data: computedData.blockHoursByFlightReg,
                            title: defaultTitle,
                            bottomLegend: "Flight Reg",
                            leftLegend: "Hours",
                            digit: 0,
                            bottomOffSet: 5,
                            bottomLegendOffSet: 5,
                            barTranslateXOffSet: 120,
                            lineTranslateXOffSet: -5,
                        },
                    },
                    { 
                        component: SortStackedBarChart, 
                        props: {
                            data: computedData.blockHoursByFlightRegSectorType,
                            title: defaultTitle,
                            bottomLegend: "Flight Reg",
                            leftLegend: "Hours",
                            digit: 0,
                            bottomOffSet: 5,
                            bottomLegendOffSet: 5,
                            itemWidthOffSet: 10,
                        },
                    },
                    { 
                        component: SortStackedBarChart, 
                        props: {
                            data: computedData.blockHoursByFlightRegFlightType,
                            title: defaultTitle,
                            bottomLegend: "Flight Reg",
                            leftLegend: "Hours",
                            digit: 0,
                            bottomOffSet: 5,
                            bottomLegendOffSet: 5,
                            translateXOffSet: -15, 
                            itemWidthOffSet: 25,
                        },
                    },            
                    { 
                        component: SortStackedBarChart, 
                        props: {
                            data: computedData.blockHoursByFlightRegCountry,
                            title: defaultTitle,
                            bottomLegend: "Flight Reg",
                            leftLegend: "Hours",
                            digit: 0,
                            bottomOffSet: 5,
                            bottomLegendOffSet: 5,
                        },
                    },
                ];

            case "blockHoursByACType":
                return [
                    {
                        component: PieChart,
                        props: {
                            data: computedData.blockHoursByAC_Type,
                            title: defaultTitle,
                            bottomLegend: "AC Type",
                            leftLegend: "Block Hours",
                            digit: 0,
                            itemWidthOffSet: isMobile ? -50 : -30,
                            translateXOffSet: isMobile ? -15 : 0,
                        },
                        style: {},
                    },
                    {
                        component: SortBarMultiLinesChart,
                        props: {
                            data: computedData.blockHoursByACType,
                            title: defaultTitle,
                            bottomLegend: "AC Type",
                            leftLegend: "Hours",
                            digit: 0,
                            barTranslateXOffSet: 120,
                            lineTranslateXOffSet: -5,
                        },
                    },     
                    { 
                        component: SortStackedBarChart, 
                        props: {
                            data: computedData.blockHoursByACTypeSectorType,
                            title: defaultTitle,
                            bottomLegend: "AC Type",
                            leftLegend: "Hours",
                            digit: 0,
                            itemWidthOffSet: 10,
                        },
                    },
                    { 
                        component: SortStackedBarChart, 
                        props: {
                            data: computedData.blockHoursByACTypeFlightType,
                            title: defaultTitle,
                            bottomLegend: "AC Type",
                            leftLegend: "Hours",
                            digit: 0,
                            translateXOffSet: -15, 
                            itemWidthOffSet: 25,
                        },
                    },
                    { 
                        component: SortStackedBarChart, 
                        props: {
                            data: computedData.blockHoursByACTypeCountry,
                            title: defaultTitle,
                            bottomLegend: "AC Type",
                            leftLegend: "Hours",
                            digit: 0,
                        },
                    },       
                ];

            case "blockHoursByCountry":
                return [
                    ...(computedData.blockHoursByCountry_Name?.length < 10 ? [{
                        component: PieChart,
                        props: {
                            data: computedData.blockHoursByCountry_Name,
                            title: defaultTitle,
                            bottomLegend: "Country",
                            leftLegend: "Block Hours",
                            digit: 0,
                            itemsSpacingOffSet: -15,
                        },
                        style: {},
                    }] : []),
                    {
                        component: SortBarMultiLinesChart,
                        props: {
                            data: computedData.blockHoursByCountryName,
                            title: defaultTitle,
                            bottomLegend: "Country",
                            leftLegend: "Hours",
                            digit: 0,
                            bottomOffSet: isMobile ? 55 : 45, 
                            bottomLegendOffSet: isMobile ? 45 : 45,
                            barTranslateXOffSet: 120,
                            lineTranslateXOffSet: 0,
                        },
                    },   
                    { 
                        component: SortStackedBarChart, 
                        props: {
                            data: computedData.blockHoursByCountrySectorType,
                            title: defaultTitle,
                            bottomLegend: "Country",
                            leftLegend: "Hours",
                            digit: 0,
                            bottomOffSet: isMobile ? 55 : 45, 
                            bottomLegendOffSet: isMobile ? 45 : 45,
                        },
                    },
                    { 
                        component: SortStackedBarChart, 
                        props: {
                            data: computedData.blockHoursByCountryFlightType,
                            title: defaultTitle,
                            bottomLegend: "Country",
                            leftLegend: "Hours",
                            digit: 0,
                            bottomOffSet: isMobile ? 55 : 45, 
                            bottomLegendOffSet: isMobile ? 45 : 45,
                            translateXOffSet: -15, 
                            itemWidthOffSet: 25,
                        },
                    },
                    { 
                        component: SortStackedBarChart, 
                        props: {
                            data: computedData.blockHoursByCountryACType,
                            title: defaultTitle,
                            bottomLegend: "Country",
                            leftLegend: "Hours",
                            digit: 0,
                            bottomOffSet: isMobile ? 55 : 45, 
                            bottomLegendOffSet: isMobile ? 45 : 45,
                        },
                    },         
                ];

            case "blockHoursByFlightNo":
                return [
                    {
                        component: SortBarMultiLinesChart,
                        props: {
                            data: computedData.blockHoursByFlightNo,
                            title: defaultTitle,
                            bottomLegend: "Flight No",
                            leftLegend: "Hours",
                            digit: 0,
                            bottomOffSet: 5,
                            bottomLegendOffSet: 5,
                            barTranslateXOffSet: 120,
                            lineTranslateXOffSet: -5,
                        },
                    },
                    { 
                        component: SortStackedBarChart, 
                        props: {
                            data: computedData.blockHoursByFlightNoSectorType,
                            title: defaultTitle,
                            bottomLegend: "Flight No",
                            leftLegend: "Hours",
                            digit: 0,
                            bottomOffSet: 5,
                            bottomLegendOffSet: 5,
                            itemWidthOffSet: 10,
                        },
                    },
                    { 
                        component: SortStackedBarChart, 
                        props: {
                            data: computedData.blockHoursByFlightNoFlightType,
                            title: defaultTitle,
                            bottomLegend: "Flight No",
                            leftLegend: "Hours",
                            digit: 0,
                            bottomOffSet: 5,
                            bottomLegendOffSet: 5,
                            translateXOffSet: -15, 
                            itemWidthOffSet: 25,
                        },
                    },
                    { 
                        component: SortStackedBarChart, 
                        props: {
                            data: computedData.blockHoursByFlightNoCountry,
                            title: defaultTitle,
                            bottomLegend: "Flight No",
                            leftLegend: "Hours",
                            digit: 0,
                            bottomOffSet: 5,
                            bottomLegendOffSet: 5,
                            itemWidthOffSet: 0, 
                        },
                    },
                ];

            default:
                return [];
        }
    }, [view, isMobile, defaultTitle, computedData]);

    const renderChart = useCallback((
        chart, 
        index, 
        customStyles = {}, 
        onDoubleClick = null
    ) => {
        const { component: Component, props = {}, style = {} } = chart;

        return (
            <Box
                key={index}
                sx={{
                    ...styles,
                    ...customStyles,
                }}
                onDoubleClick={onDoubleClick}
            >
                <Box 
                    height="100%" 
                    width={"100%"} 
                    sx={style}
                >
                    {typeof Component === 'function' 
                        ? <Component {...props} /> 
                        : Component
                    }
                </Box>                
            </Box>
        );
    }, []);    

    const renderCharts = useCallback((customStyles = {}) => {
        if (!chartMap || chartMap.length === 0) {
            return (
                <Box sx={{ ...styles, ...customStyles }}>
                    <Typography>No charts available</Typography>
                </Box>
            );
        }

        return (
            <Box display="flex" flexDirection="column" gap={2}>
                {chartMap.map((chart, index) =>
                    renderChart(chart, index, customStyles, () => {
                        setSelectedChartIndex(index);
                        setChartOpen(true);
                    })
                )}
            </Box>
        );
    }, [renderChart, chartMap]);
    
    const dialogChart = useMemo(() => {
        if (!chartOpen || !chartMap || !chartMap[selectedChartIndex]) return null;
        return renderChart(chartMap[selectedChartIndex], selectedChartIndex, {});
    }, [chartOpen, chartMap, selectedChartIndex, renderChart]);

    
    return (
        <Box className="main-container-box">                  
            <FlexBetween>
                <Header
                    title="Block Hours"
                    subtitle={displayType === 'chart' ? 'List of Charts' : 'List of Tableaus'}
                />     
                <Box display="flex" alignItems="center" gap={1}>
                    {!isMobile && (
                        <FormControl 
                            size="small" 
                            sx={{ width: isMobile ? '6rem' : '9rem' }}
                        >
                            <InputLabel>Type</InputLabel>
                            <Select
                                label="Type"
                                value={type}
                                onChange={handleTypeChange}
                            >
                                {typeOptions.map((item) => (
                                    <MenuItem key={item.value} value={item.value}>
                                        {item.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <FormControl 
                        size="small" 
                        sx={{ width: isMobile ? "6rem" : "8rem" }}
                    >
                        <InputLabel>Display</InputLabel>
                        <Select
                            value={displayType}
                            label="Display"
                            onChange={(e) => setDisplayType(e.target.value)}
                        >
                            {displayOptions.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl
                        size="small"
                        sx={{ width: isMobile ? '7rem' : '12rem' }}
                    >
                        <InputLabel id="block-hours-view-label">View</InputLabel>
                        <Select
                            labelId="block-hours-view-label"
                            label="View"
                            value={safeView}
                            onChange={(e) => setView(e.target.value)}
                        >
                            {viewOptions[displayType].map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FilterButton
                        filters={filters}
                        onClick={handleFilterOpen}
                    />
                </Box>
            </FlexBetween> 
            <Box
                display="grid"                        
                gridTemplateColumns="repeat(12, minmax(0, 1fr))"                   
            >
                <Box
                    ref={scrollRef}
                    height={LAYOUT_HEIGHT}
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    sx={{ gridColumn: "span 12", overflowY: 'auto' }}
                    onDoubleClick={displayType === 'chart' ? handleOpen : undefined}
                >
                    {displayType === 'chart'
                        ? renderCharts({ minHeight: LAYOUT_HEIGHT })
                        : (
                            <Box sx={{ ...styles, minHeight: LAYOUT_HEIGHT }}>
                                <Box height="100%" width="100%">
                                    <CommonTableau data={tableauData} mode="blockHours" />
                                </Box>
                            </Box>
                        )
                    }
                </Box>

                <ScrollToTop scrollRef={scrollRef} />
            </Box>

            {isFilterDialogOpen &&
                <FlightFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={acTypeOptions} 
                    flightRegOptions={flightRegOptions}
                    sectorTypeOptions={sectorTypeOptions}
                    departureTypeOptions={departureTypeOptions}
                    arrivalTypeOptions={arrivalTypeOptions}
                    currentFilters={filters}
                />
            }

            {chartOpen && (
                <ChartDialog
                    open={chartOpen}
                    onClose={handleClose}
                    chartComponent={dialogChart}
                />
            )}
        </Box>
    );
};

export default BlockHoursChart;